import React, { useEffect, useState, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { CONTACT, SOCIAL, SERVICES, THERAPISTS } from '../mock';
import logoFull from '../assets/logo-full.png';

const NAV = [
  { label: 'Home', to: '/', children: [] },
  { label: 'Our Philosophy', to: '/philosophy', children: [] },
  {
    label: 'About', to: '/about',
    children: [
      { label: 'Our Story', to: '/about' },
      { label: 'Why CCC', to: '/why-ccc' },
      { label: 'Kind Words', to: '/why-ccc#testimonials' },
    ],
  },
  {
    label: 'Meet the Team', to: '/team',
    children: [
      { label: 'The Whole Team', to: '/team' },
      ...THERAPISTS.map((t) => ({ label: t.name, to: `/team/${t.slug}` })),
    ],
  },
  {
    label: 'Services', to: '/services',
    children: SERVICES.map((s) => ({ label: s.title, to: `/services/${s.slug}` })),
  },
  {
    label: 'Resources', to: '/blog',
    children: [
      { label: 'Blog & Articles', to: '/blog' },
      { label: 'Existing Clients', to: '/existing-clients' },
      { label: 'Good Faith Estimate', to: '/blog/good-faith-estimate' },
    ],
  },
  {
    label: 'Insurance & FAQs', to: '/insurance',
    children: [
      { label: 'Insurance & Billing', to: '/insurance' },
      { label: 'FAQs', to: '/insurance#faqs' },
    ],
  },
  {
    label: 'Contact', to: '/contact',
    children: [
      { label: 'Contact Us', to: '/contact' },
      { label: 'Schedule an Appointment', to: '/schedule' },
      { label: 'Existing Client Scheduling', to: '/existing-clients' },
    ],
  },
];

function NavItem({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const openMenu = () => { clearTimeout(timeoutRef.current); setOpen(true); };
  const closeMenu = () => { timeoutRef.current = setTimeout(() => setOpen(false), 140); };

  const hasChildren = item.children && item.children.length > 0;

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        onClick={onNavigate}
        className={({ isActive }) => `nav-link inline-flex items-center gap-1 ${isActive ? 'active' : ''}`}
      >
        {item.label}
        {hasChildren && <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }} />}
      </NavLink>
      {hasChildren && open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-40">
          <div className="min-w-[240px] rounded-2xl bg-white shadow-[0_20px_50px_-20px_rgba(78,87,69,0.35)] border py-2 mobile-menu-enter" style={{ borderColor: 'var(--soft-border)' }}>
            {item.children.map((c) => (
              <Link
                key={c.label}
                to={c.to}
                onClick={onNavigate}
                className="block px-5 py-2 text-sm text-[color:var(--deep-sage)] hover:bg-[color:var(--cream)] hover:text-[color:var(--dusty-rose)] transition"
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMobileExpanded(null); window.scrollTo({ top: 0, behavior: 'instant' }); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 shadow-[0_8px_30px_-20px_rgba(78,87,69,0.25)]' : 'bg-white/90'} backdrop-blur-md`}
      style={{ borderBottom: '1px solid var(--soft-border)' }}
    >
      {/* Top utility bar */}
      <div className="hidden md:block" style={{ background: 'linear-gradient(90deg, rgba(245,238,231,0.6), rgba(232,221,212,0.6))', borderBottom: '1px solid var(--soft-border)' }}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-1.5 flex items-center justify-between text-xs text-[color:var(--taupe-gray)]">
          <div className="flex items-center gap-4">
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-1.5 hover:text-[color:var(--dusty-rose)] transition">
              <Phone className="w-3.5 h-3.5" /> {CONTACT.phone}
            </a>
            <span className="opacity-40">|</span>
            <a href={`mailto:${CONTACT.email}`} className="hidden lg:inline hover:text-[color:var(--dusty-rose)] transition">{CONTACT.email}</a>
          </div>
          <div className="flex items-center gap-1">
            {SOCIAL.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-7 h-7 rounded-full flex items-center justify-center text-[color:var(--sage-dark)] hover:bg-white hover:text-[color:var(--dusty-rose)] transition">
                <s.Icon className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 md:py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
          <img
            src={logoFull}
            alt="Chesapeake Counseling Center — Let Your Light Shine"
            className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-14 md:h-16' : 'h-16 md:h-20 lg:h-24'} group-hover:scale-[1.03]`}
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className={`font-serif text-[color:var(--deep-sage)] transition-all duration-300 ${scrolled ? 'text-lg md:text-xl' : 'text-xl md:text-2xl lg:text-[26px]'}`}>Chesapeake Counseling Center</span>
            <span className="font-script text-[color:var(--dusty-rose)] text-sm md:text-base -mt-0.5">Let Your Light Shine</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {NAV.map((n) => (
            <NavItem key={n.label} item={n} />
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/schedule" className="btn-primary text-sm whitespace-nowrap" style={{ padding: '0.65rem 1.15rem' }}>Schedule</Link>
        </div>

        <button className="lg:hidden p-2 rounded-full hover:bg-[color:var(--cream)] transition" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6 text-[color:var(--deep-sage)]" /> : <Menu className="w-6 h-6 text-[color:var(--deep-sage)]" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t mobile-menu-enter max-h-[80vh] overflow-y-auto" style={{ borderColor: 'var(--soft-border)' }}>
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => {
              const hasChildren = n.children && n.children.length > 0;
              const isExpanded = mobileExpanded === n.label;
              return (
                <div key={n.label} className="border-b" style={{ borderColor: 'var(--soft-border)' }}>
                  <div className="flex items-center justify-between py-2.5">
                    <Link to={n.to} className="text-[15px] text-[color:var(--deep-sage)]">{n.label}</Link>
                    {hasChildren && (
                      <button onClick={() => setMobileExpanded(isExpanded ? null : n.label)} aria-label="Expand" className="p-1">
                        <ChevronDown className={`w-4 h-4 text-[color:var(--sage-dark)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  {hasChildren && isExpanded && (
                    <div className="pl-4 pb-2 flex flex-col">
                      {n.children.map((c) => (
                        <Link key={c.label} to={c.to} className="py-1.5 text-sm text-[color:var(--taupe-gray)]">{c.label}</Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="pt-4 flex flex-col gap-3">
              <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-[color:var(--sage-dark)] text-sm"><Phone className="w-4 h-4" /> {CONTACT.phone}</a>
              <Link to="/schedule" className="btn-primary text-sm justify-center">Schedule an Appointment</Link>
              <div className="flex gap-2 pt-3">
                {SOCIAL.map((s) => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-9 h-9 rounded-full bg-[color:var(--cream)] flex items-center justify-center text-[color:var(--sage-dark)] hover:bg-[color:var(--sage-green)] hover:text-white transition">
                    <s.Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
